import qs from 'qs'
import { QueryData } from '@garpix/base-api'
import { apiInterface } from '@/api/interfaces'
import AbstractApi from '@/api/abstract'
import { orderApiReq, orderApiRes } from './interface'

class OrderApi implements apiInterface.IOrderApi {
  ctx: AbstractApi

  constructor(ctx: AbstractApi) {
    this.ctx = ctx
  }

  createCertificates = async (
    params: orderApiReq.IPostCreateCertificateReq
  ): Promise<
    | orderApiRes.IPostCreateCertificateOnlinePaymentRes
    | orderApiRes.IPostCreateCertificateOfflinePaymentRes[]
  > => {
    const res = await this.ctx.post<
      orderApiReq.IPostCreateCertificateReq,
      | orderApiRes.IPostCreateCertificateOnlinePaymentRes
      | orderApiRes.IPostCreateCertificateOfflinePaymentRes[]
    >('/order/certificates/', params)
    return res.data
  }

  createDeposit = async (
    params: orderApiReq.IPostCreateDepositReq
  ): Promise<orderApiRes.IPostCreateDepositRes> => {
    const res = await this.ctx.post<
      orderApiReq.IPostCreateDepositReq,
      orderApiRes.IPostCreateDepositRes
    >('/order/create_deposit/', params)
    return res.data
  }

  createServices = async (
    params: orderApiReq.ICreateServicesReq
  ): Promise<orderApiRes.ICreateServicesRes> => {
    const res = await this.ctx.post<orderApiReq.ICreateServicesReq, orderApiRes.ICreateServicesRes>(
      '/order/create_order_services/',
      params
    )
    return res.data
  }

  loadOrders = async (
    params: QueryData<orderApiReq.ILoadOrderReq>
  ): Promise<orderApiRes.ILoadOrderRes> => {
    const { data } = await this.ctx.post<
      QueryData<orderApiReq.ILoadOrderReq>,
      orderApiRes.ILoadOrderRes
    >('/order/load_orders/', params)
    return data
  }

  getCancelReason = async (
    params: orderApiReq.IGetCancelReasonReq = {}
  ): Promise<orderApiRes.IGetCancelReasonRes> => {
    const res = await this.ctx.get<
      orderApiReq.IGetCancelReasonReq,
      orderApiRes.IGetCancelReasonRes
    >('/order/order_cancel_reason_list/', params)
    return res.data
  }

  getOrders = async (
    params: orderApiReq.IGetOrdersReq = {}
  ): Promise<orderApiRes.IGetOrdersRes> => {
    const res = await this.ctx.get<orderApiReq.IGetOrdersReq, orderApiRes.IGetOrdersRes>(
      '/order/orders/',
      params
    )
    return res.data
  }

  getOrder = async (
    id: number,
    params: orderApiReq.IGetOrderReq = {}
  ): Promise<orderApiRes.IGetOrderRes> => {
    const res = await this.ctx.get<orderApiReq.IGetOrderReq, orderApiRes.IGetOrderRes>(
      `/order/orders/${id}/`,
      params,
      {
        paramsSerializer: (params: orderApiReq.IGetOrderReq) =>
          qs.stringify(params, { arrayFormat: 'brackets' })
      }
    )
    return res.data
  }

  createOrders = async (
    params: orderApiReq.ICreateOrdersReq
  ): Promise<orderApiRes.ICreateOrdersRes[]> => {
    const res = await this.ctx.post<orderApiReq.ICreateOrdersReq, orderApiRes.ICreateOrdersRes[]>(
      '/order/orders/',
      params
    )
    return res.data
  }

  updateOrders = async (
    id: number,
    params: orderApiReq.IUpdateOrdersReq = {}
  ): Promise<orderApiRes.IUpdateOrdersRes> => {
    const res = await this.ctx.patch<orderApiReq.IUpdateOrdersReq, orderApiRes.IUpdateOrdersRes>(
      `/order/orders/${id}/`,
      params
    )
    return res.data
  }

  getOrdersDayAero = async (
    params: orderApiReq.IGetOrdersDayAeroReq = {},
    controller: AbortController
  ): Promise<orderApiRes.IGetOrdersDayAeroRes> => {
    const res = await this.ctx.get<
      orderApiReq.IGetOrdersDayAeroReq,
      orderApiRes.IGetOrdersDayAeroRes
    >('/order/orders/day_order_aero/', params, {
      signal: controller.signal
    })
    return res.data
  }

  getOrdersDaySurf = async (
    params: orderApiReq.IGetOrdersDaySurfReq = {},
    controller: AbortController
  ): Promise<orderApiRes.IGetOrdersDaySurfRes> => {
    const res = await this.ctx.get<
      orderApiReq.IGetOrdersDaySurfReq,
      orderApiRes.IGetOrdersDaySurfRes
    >('/order/orders/day_order_surf/', params, {
      signal: controller.signal
    })
    return res.data
  }

  getDayStatistic = async (
    params: orderApiReq.IGetDayStatisticReq
  ): Promise<orderApiRes.IGetOrdersDayAeroTubeRes | orderApiRes.IGetOrdersDayWaveRes> => {
    const res = await this.ctx.get<
      orderApiReq.IGetDayStatisticReq,
      orderApiRes.IGetOrdersDayAeroTubeRes | orderApiRes.IGetOrdersDayWaveRes
    >('/order/orders/day_statistic/', params)
    return res.data
  }

  getOrdersWeekAero = async (
    params: orderApiReq.IGetOrdersWeekAeroReq = {},
    controller: AbortController
  ): Promise<orderApiRes.IGetOrdersWeekAeroAndSurfRes[]> => {
    const res = await this.ctx.get<
      orderApiReq.IGetOrdersWeekAeroReq,
      orderApiRes.IGetOrdersWeekAeroAndSurfRes[]
    >('/order/orders/week_order_aero/', params, {
      signal: controller.signal
    })
    return res.data
  }

  getOrdersWeekSurf = async (
    params: orderApiReq.IGetOrdersWeekSurfReq = {},
    controller: AbortController
  ): Promise<orderApiRes.IGetOrdersWeekAeroAndSurfRes[]> => {
    const res = await this.ctx.get<
      orderApiReq.IGetOrdersWeekSurfReq,
      orderApiRes.IGetOrdersWeekAeroAndSurfRes[]
    >('/order/orders/week_order_surf/', params, {
      signal: controller.signal
    })
    return res.data
  }

  getPayOrder = async (
    params: orderApiReq.IGetPayOrderReq = {}
  ): Promise<orderApiRes.IGetPayOrderRes> => {
    const res = await this.ctx.get<orderApiReq.IGetPayOrderReq, orderApiRes.IGetPayOrderRes>(
      '/order/pay_order/',
      params
    )
    return res.data
  }

  createPayOrderOnline = async (
    params: orderApiReq.ICreatePayOrderOnlineReq[]
  ): Promise<orderApiRes.ICreatePayOrderOnlineRes> => {
    const res = await this.ctx.post<
      orderApiReq.ICreatePayOrderOnlineReq[],
      orderApiRes.ICreatePayOrderOnlineRes
    >('/order/pay_order/create_pay_order_online/', params)
    return res.data
  }

   createFlyBlock = async (
    params: orderApiReq.ICreateFlyBlockReq
  ): Promise<orderApiRes.ICreateFlyBlockRes
  > => {
    const res = await this.ctx.post<
      orderApiReq.ICreateFlyBlockReq,
      orderApiRes.ICreateFlyBlockRes
    >('/order/fly_list_time_intervals/', params)
    return res.data
  }
}

export default OrderApi
